import React, { useContext, useMemo, useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { romanize as romanizeKorean } from '@romanize/korean'
import UserContext from '../context/UserContext'
import useLearnerContext from '../hooks/useLearnerContext'
import languages, { getLanguageAcronym } from '../languages'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import { MY_SAY_ITEMS_QUERY, ADD_SAY_ITEM_MUTATION, DELETE_SAY_ITEM_MUTATION } from '../query/gqlQueries'
import ReadingFlowView from './ReadingFlowView'
import VocabChatView from './VocabChatView'
import CloseIcon from '@mui/icons-material/Close'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'

// "What I want to say": write, in your own language, what you want to be able
// to say. The server translates it into the language you're learning (DeepL),
// and the list becomes your practice — say them aloud (the Flow view) or talk
// about them in a chat. Your teachers for that language can see the list too.

const bg = '#0d0d0f'
const card = { background: '#141418', border: '1px solid #2a2a33', borderRadius: 12, padding: '0.8rem 1rem' }
const btn = { background: '#1b1b21', color: '#e8e8ec', border: '1px solid #2a2a33', borderRadius: 9, padding: '0.5rem 0.95rem', cursor: 'pointer', fontSize: '0.88rem', fontFamily: 'inherit' }
const primary = { ...btn, background: '#34d399', color: '#06281d', border: 'none', fontWeight: 700 }
const chip = (on) => ({ ...btn, borderRadius: 999, borderColor: on ? '#34d399' : '#2a2a33', color: on ? '#6ee7b7' : '#c3c3cc', background: on ? 'rgba(52,211,153,0.08)' : '#141418' })

const codeOf = (l) => (l?.code || getLanguageAcronym(l?.name || '') || '').toLowerCase()

export default function SayItView({ voiceSpeed, onClose }) {
  const { username } = useContext(UserContext)
  const { native, learning } = useLearnerContext(username)
  const learningCodes = useMemo(() => learning.filter(l => l && l.active !== false).map(codeOf).filter(Boolean), [learning])
  const nativeCode = codeOf(native[0]) || ''

  const [picked, setPicked] = useState('')
  const lang = picked || learningCodes[0] || ''
  const [draft, setDraft] = useState('')
  const [adding, setAdding] = useState(false)
  const [err, setErr] = useState('')
  const [mode, setMode] = useState(null) // 'say' | 'chat'

  const { data, error, refetch } = useQuery(MY_SAY_ITEMS_QUERY, { variables: { lang }, skip: !lang, fetchPolicy: 'cache-and-network', errorPolicy: 'all' })
  const [addItem] = useMutation(ADD_SAY_ITEM_MUTATION)
  const [deleteItem] = useMutation(DELETE_SAY_ITEM_MUTATION)
  const items = (data?.mySayItems || []).filter(i => i.translation)
  const backendOld = /Cannot query field|Unknown type/i.test(error?.message || '')
  const langName = languages[lang] || lang

  // Flashcard-shaped, with the language being learned as the side to practise.
  const cards = useMemo(() => items.map(i => ({
    sourceId: i.id, source: i.translation, sourceLanguage: i.lang,
    translations: [{ targetId: `${i.id}-o`, target: i.text, targetLanguage: i.sourceLang || nativeCode || 'eng' }],
  })), [items, nativeCode])

  if (mode === 'say') return <ReadingFlowView cards={cards} practiceLang={lang} voiceSpeed={voiceSpeed} onClose={() => setMode(null)} />
  if (mode === 'chat') return <VocabChatView cards={cards} practiceLang={lang} voiceSpeed={voiceSpeed} onClose={() => setMode(null)} />

  const add = async () => {
    setAdding(true); setErr('')
    try {
      await addItem({ variables: { text: draft, lang } })
      setDraft('')
      await refetch()
    } catch (e) { setErr(e.message || 'That did not save.') }
    setAdding(false)
  }
  const remove = async (id) => {
    try { await deleteItem({ variables: { id } }); await refetch() } catch (e) { setErr(e.message || 'Could not delete that.') }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1300, background: bg, color: '#e8e8ec', overflowY: 'auto', fontFamily: 'inherit' }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '1.5rem 1.2rem 3rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
          <div>
            <div style={{ color: '#8a8a95', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Practice</div>
            <h2 style={{ margin: '0.2rem 0 0', fontSize: '1.6rem' }}>What I want to say</h2>
          </div>
          <button style={{ ...btn, padding: '0.45rem' }} onClick={onClose} aria-label="Close"><CloseIcon /></button>
        </div>
        <p style={{ color: '#8a8a95', lineHeight: 1.5, margin: '0.8rem 0 1.2rem' }}>
          Write what you'd like to be able to say, in your own language. It's translated for you, then you can
          practise saying it aloud or talk it through. Your {langName} teacher can see this list too.
        </p>

        {backendOld && <div style={{ color: '#fbbf24', marginBottom: '1rem' }}>The GraphQL server hasn't been redeployed with this feature yet.</div>}
        {learningCodes.length === 0 && <div style={{ ...card, color: '#8a8a95' }}>Add a language you're learning in your profile first.</div>}

        {learningCodes.length > 0 && (
          <>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              {learningCodes.map(c => (
                <button key={c} style={chip(c === lang)} onClick={() => setPicked(c)}>{languages[c] || c}</button>
              ))}
            </div>

            <textarea
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && draft.trim() && !adding) add() }}
              placeholder="e.g. I'd like a table by the window, please."
              style={{ width: '100%', boxSizing: 'border-box', minHeight: 80, resize: 'vertical', background: '#141418', color: '#e8e8ec', border: '1px solid #2a2a33', borderRadius: 12, padding: '0.75rem 0.9rem', font: 'inherit', fontSize: '1rem', lineHeight: 1.5 }}
            />
            <div style={{ display: 'flex', gap: '0.7rem', alignItems: 'center', margin: '0.6rem 0 1.4rem', flexWrap: 'wrap' }}>
              <button style={primary} disabled={!draft.trim() || adding} onClick={add}>{adding ? 'Translating…' : `Add — in ${langName}`}</button>
              {err && <span style={{ color: '#f87171', fontSize: '0.88rem' }}>{err}</span>}
            </div>

            {items.length > 0 && (
              <div style={{ display: 'flex', gap: '0.6rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <button style={primary} onClick={() => setMode('say')}>🎤 Say them aloud</button>
                <button style={btn} onClick={() => setMode('chat')}>💬 Talk about them</button>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
              {items.length === 0 && !backendOld && <div style={{ ...card, color: '#8a8a95' }}>Nothing yet in {langName}. Start with something you said this week and wished you could say in {langName}.</div>}
              {items.map(i => (
                <div key={i.id} style={{ ...card, display: 'flex', gap: '0.8rem', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '1.15rem', fontWeight: 600, color: '#e8e8ec' }}>{i.translation}</div>
                    {i.lang === 'kor' && <div style={{ color: '#8a8a95', fontSize: '0.88rem', marginTop: '0.15rem' }}>{romanizeKorean(i.translation)}</div>}
                    <div style={{ color: '#8a8a95', fontSize: '0.88rem', marginTop: '0.3rem' }}>{i.text}</div>
                  </div>
                  <button style={{ ...btn, padding: '0.4rem' }} aria-label="Hear it" onClick={() => speak(i.translation, voices[i.lang] || voices.eng, voiceSpeed)}><VolumeUpIcon fontSize="small" /></button>
                  <button style={{ ...btn, padding: '0.4rem 0.6rem', color: '#8a8a95' }} onClick={() => remove(i.id)}>Remove</button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
